import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'
import type { LifecyclePhase, MilestoneAnchor } from './phases'

export interface Milestone {
  id: string
  title: string
  description: string | null
  phase: LifecyclePhase
  phaseDisplayName: string
  offsetDays: number
  anchor: MilestoneAnchor
  offsetLabel: string
  ownerRole: string | null
  sortOrder: number
}

export interface TimelineTemplateListItem {
  id: string
  familyId: string
  name: string
  description: string | null
  version: number
  status: 'Draft' | 'Published'
  milestoneCount: number
  isRetired: boolean
  hasPublishedVersion: boolean
  latestPublishedVersion: number | null
  /** The version a plan is built from, which is not the row's own id once a draft is open. */
  latestPublishedVersionId: string | null
  isUsedByAPlan: boolean
}

export interface TimelineTemplateDetail {
  id: string
  familyId: string
  name: string
  description: string | null
  version: number
  status: 'Draft' | 'Published'
  isRetired: boolean
  isUsedByAPlan: boolean
  createdAt: string
  publishedAt: string | null
  milestones: Milestone[]
}

export interface MilestoneInput {
  title: string
  description: string | null
  phase: LifecyclePhase
  offsetDays: number
  anchor: MilestoneAnchor
  ownerRole: string | null
}

const root = '/api/timeline-templates'
const listKey = ['timeline-templates'] as const
const detailKey = (id: string) => ['timeline-templates', id] as const

export function useTimelineTemplates() {
  return useQuery({
    queryKey: listKey,
    queryFn: () => apiFetch<TimelineTemplateListItem[]>(root),
  })
}

export function useTimelineTemplate(id: string | undefined) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => apiFetch<TimelineTemplateDetail>(`${root}/${id}`),
    enabled: Boolean(id),
  })
}

function useTemplateMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    // Any change can produce a new version, so both the list and the open
    // template are refetched rather than patched.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey }),
  })
}

export function useCreateTimelineTemplate() {
  return useTemplateMutation((body: { name: string; description: string | null }) =>
    apiFetch<TimelineTemplateDetail>(root, { method: 'POST', body }),
  )
}

export function useUpdateTimelineTemplate(id: string) {
  return useTemplateMutation((body: { name: string; description: string | null }) =>
    apiFetch<TimelineTemplateDetail>(`${root}/${id}`, { method: 'PUT', body }),
  )
}

export function usePublishTimelineTemplate(id: string) {
  return useTemplateMutation(() =>
    apiFetch<TimelineTemplateDetail>(`${root}/${id}/publish`, { method: 'POST' }),
  )
}

export function useCloneTimelineTemplate(id: string) {
  return useTemplateMutation((body: { name: string }) =>
    apiFetch<TimelineTemplateDetail>(`${root}/${id}/clone`, { method: 'POST', body }),
  )
}

export function useDeleteTimelineTemplate(id: string) {
  return useTemplateMutation(() => apiFetch<void>(`${root}/${id}`, { method: 'DELETE' }))
}

export function useRetireTimelineTemplate(id: string) {
  return useTemplateMutation(() => apiFetch<void>(`${root}/${id}/retire`, { method: 'POST' }))
}

export function useAddMilestone(templateId: string) {
  return useTemplateMutation((body: MilestoneInput) =>
    apiFetch<Milestone>(`${root}/${templateId}/milestones`, { method: 'POST', body }),
  )
}

export function useUpdateMilestone(templateId: string) {
  return useTemplateMutation(({ id, ...body }: MilestoneInput & { id: string }) =>
    apiFetch<Milestone>(`${root}/${templateId}/milestones/${id}`, { method: 'PUT', body }),
  )
}

export function useDeleteMilestone(templateId: string) {
  return useTemplateMutation((milestoneId: string) =>
    apiFetch<void>(`${root}/${templateId}/milestones/${milestoneId}`, { method: 'DELETE' }),
  )
}

/** Groups milestones by phase, preserving the server's process order. */
export function groupByPhase(milestones: Milestone[]): { phase: string; items: Milestone[] }[] {
  const groups: { phase: string; items: Milestone[] }[] = []

  for (const milestone of milestones) {
    const last = groups.at(-1)
    if (last && last.phase === milestone.phaseDisplayName) {
      last.items.push(milestone)
    } else {
      groups.push({ phase: milestone.phaseDisplayName, items: [milestone] })
    }
  }

  return groups
}
